import React from 'react';

interface Props {
  label: string;
  hint?: string;
  /**
   * Render the hint under the control instead of beside the label.
   * Use for longer hints — keeps labels from wrapping and keeps inputs in
   * multi-column rows aligned regardless of hint length.
   */
  hintBelow?: boolean;
  /** One-line explanation rendered directly under the label, full width — for hints too long to sit beside it. */
  subtitle?: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
  htmlFor?: string;
}

/**
 * `data-copilot-*` expose the label and its help text to the in-app copilot so
 * it can answer "what is this field for?" from the hint YOU wrote, rather than
 * guessing. Only the label and hint are exposed — never the field's value.
 */
export const FormField: React.FC<Props> = ({ label, hint, hintBelow, subtitle, error, required, children, htmlFor }) => (
  <div className="block mb-4" data-copilot-field={label} data-copilot-hint={hint || subtitle || undefined}>
    <label htmlFor={htmlFor} className={`flex items-baseline justify-between gap-3 ${subtitle ? 'mb-0.5' : 'mb-1.5'}`}>
      {/* shrink-0 + nowrap: a long hint wraps on the right instead of squeezing the label onto two lines */}
      <span className="shrink-0 whitespace-nowrap text-[13px] font-medium text-text-secondary">
        {label}{required && <span className="text-primary ml-0.5">*</span>}
      </span>
      {hint && !hintBelow && !error && <span className="min-w-0 text-right text-2xs text-text-muted">{hint}</span>}
    </label>
    {subtitle && <p className="mb-2 text-2xs leading-relaxed text-text-muted">{subtitle}</p>}
    {children}
    {hint && hintBelow && !error && (
      <span className="block mt-1.5 text-2xs text-text-muted">{hint}</span>
    )}
    {error && (
      <span className="flex items-center gap-1.5 mt-1.5 text-xs text-danger">
        <span className="inline-block w-1 h-1 rounded-full bg-danger" />
        {error}
      </span>
    )}
  </div>
);
